/**
 * Hook: Retrieval progress for refresh flow (FULL_ORG_REFRESH | METADATA_TYPE)
 *
 * Uses GET /api/retrievals/:id/refresh. Same SSE shape for both refresh types.
 * Requires RetrievalProgressProvider.
 *
 * Resilience:
 * - Watchdog timer: if no event arrives within WATCHDOG_TIMEOUT_MS, the connection
 *   is silently dead — reconnect to wake the backend back up.
 * - Reconnect on close: if the connection closes without a `complete` event, keep
 *   reconnecting until the retrieval reaches 100%.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useRetrievalProgressContext } from '@/dataProvider/retrievals';
import type { RefreshProgressData, RetrievalCompleteData } from '@/dataProvider/retrievals';
import type { RetrievalSSEEventType } from '@/dataProvider/retrievals';

const WATCHDOG_TIMEOUT_MS = 30_000;

export interface UseRetrievalProgressRefreshReturn {
  status: string | null;
  message: string | null;
  progressPercentage: number;
  currentPhase: string | null;
  totalItems: number;
  processedItems: number;
  retrievalType: 'full_org_refresh' | 'metadata_type' | null;
  phaseDetails: RefreshProgressData['phase_details'] | null;
  isConnected: boolean;
  isComplete: boolean;
  connectionError: string | null;
  isUnauthenticated: boolean;
  lastEvent: RefreshProgressData | RetrievalCompleteData | null;
  connect: (retrievalId: string) => void;
  disconnect: () => void;
}

export function useRetrievalProgressRefresh(): UseRetrievalProgressRefreshReturn {
  const { connectRefresh } = useRetrievalProgressContext();

  const [status, setStatus] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [processedItems, setProcessedItems] = useState(0);
  const [retrievalType, setRetrievalType] = useState<'full_org_refresh' | 'metadata_type' | null>(null);
  const [phaseDetails, setPhaseDetails] = useState<RefreshProgressData['phase_details']>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isUnauthenticated, setIsUnauthenticated] = useState(false);
  const [lastEvent, setLastEvent] = useState<RefreshProgressData | RetrievalCompleteData | null>(null);

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const currentRetrievalIdRef = useRef<string | null>(null);
  const watchdogTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCompleteRef = useRef(false);
  // Stable ref to latest connect — used by watchdog/close callbacks to avoid stale closures
  const connectRef = useRef<((id: string) => void) | null>(null);

  const resetState = useCallback(() => {
    setStatus(null);
    setMessage(null);
    setProgressPercentage(0);
    setCurrentPhase(null);
    setTotalItems(0);
    setProcessedItems(0);
    setRetrievalType(null);
    setPhaseDetails(null);
    setIsComplete(false);
    setConnectionError(null);
    setIsUnauthenticated(false);
    setLastEvent(null);
  }, []);

  const clearWatchdog = useCallback(() => {
    if (watchdogTimerRef.current) {
      clearTimeout(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
  }, []);

  const disconnect = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    clearWatchdog();
    currentRetrievalIdRef.current = null;
    isCompleteRef.current = false;
    setIsConnected(false);
    resetState();
  }, [resetState, clearWatchdog]);

  const connect = useCallback(
    (retrievalId: string) => {
      if (!retrievalId) {
        setConnectionError('No retrieval ID provided');
        return;
      }

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      clearWatchdog();
      resetState();
      setIsConnected(false);

      isCompleteRef.current = false;
      currentRetrievalIdRef.current = retrievalId;

      // Arms (or re-arms) the inactivity watchdog.
      // If WATCHDOG_TIMEOUT_MS passes with no event, the stream is silently dead —
      // reconnect to wake the backend.
      const armWatchdog = () => {
        clearWatchdog();
        watchdogTimerRef.current = setTimeout(() => {
          const id = currentRetrievalIdRef.current;
          if (id && !isCompleteRef.current) {
            connectRef.current?.(id);
          }
        }, WATCHDOG_TIMEOUT_MS);
      };

      unsubscribeRef.current = connectRefresh(retrievalId, {
        onOpen: () => {
          setIsConnected(true);
          setConnectionError(null);
          armWatchdog();
        },
        onEvent: (event: RetrievalSSEEventType, data: RefreshProgressData | RetrievalCompleteData) => {
          armWatchdog(); // Reset inactivity timer on every event
          setLastEvent(data as RefreshProgressData | RetrievalCompleteData);
          setStatus(data.status);

          if (event === 'complete') {
            isCompleteRef.current = true;
            clearWatchdog();
            setIsComplete(true);
            setIsConnected(false);
            if ('message' in data) setMessage(data.message ?? null);
            return;
          }

          if (event === 'progress_update' && 'progress_percentage' in data) {
            const d = data as RefreshProgressData;
            setMessage(d.message ?? null);
            setProgressPercentage(d.progress_percentage);
            setCurrentPhase(d.current_phase ?? null);
            setTotalItems(d.total_items ?? 0);
            setProcessedItems(d.processed_items ?? 0);
            setRetrievalType(d.retrieval_type ?? null);
            setPhaseDetails(d.phase_details ?? null);
          }
        },
        onClose: () => {
          clearWatchdog();
          setIsConnected(false);

          const id = currentRetrievalIdRef.current;
          if (!isCompleteRef.current && id) {
            setTimeout(() => {
              if (currentRetrievalIdRef.current === id && !isCompleteRef.current) {
                connectRef.current?.(id);
              }
            }, 2_000);
          }
        },
        onError: (err) => {
          clearWatchdog();
          setConnectionError(err instanceof Error ? err.message : String(err));
          setIsConnected(false);
        },
        onUnauthenticated: () => {
          clearWatchdog();
          isCompleteRef.current = true;
          setIsUnauthenticated(true);
          setIsConnected(false);
        },
      });
    },
    [connectRefresh, resetState, clearWatchdog]
  );

  // Keep connectRef pointing at the latest connect so watchdog/close callbacks never go stale
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    return () => disconnect();
  }, [disconnect]);

  return {
    status,
    message,
    progressPercentage,
    currentPhase,
    totalItems,
    processedItems,
    retrievalType,
    phaseDetails,
    isConnected,
    isComplete,
    connectionError,
    isUnauthenticated,
    lastEvent,
    connect,
    disconnect,
  };
}
